import React, { useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import '../../styles/auth.css';
import logoPng from '../../assets/images/logo.png';
import http from '../../lib/http';
import { toast } from 'react-toastify';

const ArrowIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

const BackIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="19" y1="12" x2="5" y2="12" />
    <polyline points="12 19 5 12 12 5" />
  </svg>
);

const ShieldIcon = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <polyline points="9 12 11 14 15 10" />
  </svg>
);

function EmailVerification() {
  const location = useLocation();
  const navigate = useNavigate();

  const email = location.state?.email || localStorage.getItem('otp_email') || '';
  // 'register' -> auth:sanctum-protected /verify-otp, /resend-otp (needs a Bearer token from /register)
  // 'forgot-password' -> public forgot-password/verify-otp (no token needed, Cache-backed, 10 min TTL)
  const mode = location.state?.mode || 'register';
  const isForgotPassword = mode === 'forgot-password';

  // ASSUMPTION: register() returns { token }, and the register screen is expected to pass it
  // through here (location.state.token) or stash it in localStorage under 'otp_token'.
  // Adjust the key below to match whatever RegisterPage.jsx actually uses today.
  const registerToken = location.state?.token || localStorage.getItem('otp_token') || '';

  const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (index, value) => {
    const digit = value.replace(/\D/g, '').slice(0, 1);

    const next = [...verificationCode];
    next[index] = digit;
    setVerificationCode(next);
    setError('');

    if (digit && index < 5) {
      document.getElementById(`otp-${index + 1}`)?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
      document.getElementById(`otp-${index - 1}`)?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();

    const pasted = e.clipboardData
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, 6);

    if (!pasted) return;

    const next = ['', '', '', '', '', ''];
    pasted.split('').forEach((digit, index) => {
      next[index] = digit;
    });

    setVerificationCode(next);
    setError('');

    const focusIndex = pasted.length >= 6 ? 5 : pasted.length;
    document.getElementById(`otp-${focusIndex}`)?.focus();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email) {
      setError('Email is missing. Please try again from login or forgot password.');
      return;
    }

    const code = verificationCode.join('');

    if (code.length !== 6) {
      setError('Please enter the 6-digit code.');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      if (isForgotPassword) {
        // Public route — Cache-backed, no auth needed.
        await http.post('/api/customer/forgot-password/verify-otp', {
          email,
          otp: code,
        });

        toast.success('Code verified. Please set your new password.');
        // Reset-password step needs the email again to call /forgot-password/reset.
        navigate('/reset-password', { replace: true, state: { email } });
      } else {
        // auth:sanctum route — needs the Bearer token issued by /register.
        if (!registerToken) {
          setError('Session expired. Please register again.');
          setSubmitting(false);
          return;
        }

        await http.post(
          '/api/customer/verify-otp',
          { otp: code },
          { headers: { Authorization: `Bearer ${registerToken}` } }
        );

        toast.success('Verification successful. Please log in to continue.');
        localStorage.removeItem('otp_email');
        localStorage.removeItem('otp_token');
        navigate('/login', { replace: true });
      }
    } catch (err) {
      const msg = err?.response?.data?.message || 'Invalid or expired OTP. Please try again.';
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendCode = async () => {
    if (!email) {
      setError('Email is missing. Please try again from login or forgot password.');
      return;
    }

    setResending(true);
    setError('');

    try {
      if (isForgotPassword) {
        // There's no dedicated forgot-password resend route — re-hitting /request
        // regenerates and re-sends the OTP the same way the original request did.
        await http.post('/api/customer/forgot-password/request', { email });
      } else {
        if (!registerToken) {
          setError('Session expired. Please register again.');
          setResending(false);
          return;
        }

        await http.post(
          '/api/customer/resend-otp',
          {},
          { headers: { Authorization: `Bearer ${registerToken}` } }
        );
      }

      toast.info('A new code has been sent to your email.');
      setVerificationCode(['', '', '', '', '', '']);
      document.getElementById('otp-0')?.focus();
    } catch (err) {
      const msg = err?.response?.data?.message || 'Could not resend OTP. Please try again.';
      setError(msg);
      toast.error(msg);
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-left">
        <div className="auth-left-content">
          <div className="auth-left-logo auth-left-logo-xl">
            <img src={logoPng} alt="Easy World Online" />
          </div>

          <h1 className="auth-left-title">
            Verify your<br />email.
          </h1>

          <p className="auth-left-sub">
            Enter the secure 6-digit OTP sent to your email to continue.
          </p>

          <div className="auth-left-pills">
            <span className="auth-pill">🔒 Secure OTP</span>
            <span className="auth-pill">📧 Email Verification</span>
            <span className="auth-pill">⚡ Quick Access</span>
          </div>
        </div>

        <div className="auth-orb auth-orb-1" />
        <div className="auth-orb auth-orb-2" />
        <div className="auth-orb auth-orb-3" />
      </div>

      <div className="auth-right">
        <div className="auth-card forgot-card-layout">
          <div className="auth-card-header">
            <div className="auth-card-logo">
              <img src={logoPng} alt="Easy World" />
            </div>

            <div>
              <h2 className="auth-card-title">Check your email</h2>
              <p className="auth-card-sub">Enter the 6-digit verification code</p>
            </div>
          </div>

          <div className="fp-illustration">
            <div className="fp-illustration-icon">
              <ShieldIcon />
            </div>

            <div className="fp-illustration-text">
              <div className="fp-illustration-title">Email verification required</div>
              <div className="fp-illustration-sub">
                We sent a 6-digit code to {email || 'your email address'}.
              </div>
            </div>
          </div>

          {error && (
            <div className="auth-error">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="auth-form" style={{ marginBottom: 0 }}>
            <div className="otp-container" onPaste={handlePaste}>
              {verificationCode.map((digit, index) => (
                <input
                  key={index}
                  id={`otp-${index}`}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  className="otp-input"
                  value={digit}
                  onChange={(e) => handleInputChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  maxLength="1"
                  disabled={submitting}
                  autoFocus={index === 0}
                  required
                />
              ))}
            </div>

            <button
              type="submit"
              className="auth-btn auth-btn-primary"
              disabled={submitting}
              style={{ marginTop: 18 }}
            >
              {submitting ? (
                <>
                  <span className="auth-btn-spinner" />&nbsp;Verifying…
                </>
              ) : (
                <>
                  Verify Code &nbsp;<ArrowIcon />
                </>
              )}
            </button>
          </form>

          <p className="auth-footer-text" style={{ marginTop: 20 }}>
            Haven&apos;t got the code yet?{' '}
            <button
              className="auth-link"
              onClick={handleResendCode}
              type="button"
              disabled={resending}
              style={{ background: 'transparent', border: 0, padding: 0, cursor: 'pointer' }}
            >
              {resending ? 'Sending...' : 'Resend code'}
            </button>
          </p>

          <Link
            to="/login"
            className="auth-btn auth-btn-ghost"
            style={{ marginTop: 10, textDecoration: 'none', display: 'flex' }}
          >
            <BackIcon />&nbsp; Back to login
          </Link>
        </div>
      </div>
    </div>
  );
}

export default EmailVerification;